import { useState } from "react";
import { supabase } from "../supabaseClient";

export function useExamAttempt(session) {
  const [currentAttemptId, setCurrentAttemptId] = useState(null);
  const [submittingExam, setSubmittingExam] = useState(false);

  const [viewReportAnswers, setViewReportAnswers] = useState([]);
  const [loadingViewReport, setLoadingViewReport] = useState(false);

  const startExamAttempt = async (examSessionId, totalQuestions, retakePermissionId = null) => {
    if (!session?.user?.id) {
      alert("Please log in first.");
      return null;
    }

    const { data, error } = await supabase
      .from("exam_attempts")
      .insert({
        exam_session_id: examSessionId,
        user_id: session.user.id,
        total_questions: totalQuestions,
        attempt_status: "in_progress",
        attempt_type: retakePermissionId ? "retake" : "normal",
        retake_permission_id: retakePermissionId || null,
      })
      .select("id")
      .single();

    if (error) {
      alert(error.message);
      return null;
    }

    setCurrentAttemptId(data.id);
    return data.id;
  };

  const submitExamAttempt = async ({ attemptId, questions, answers, retakePermissionId }) => {
    if (!attemptId) {
      alert("Missing exam attempt.");
      return false;
    }

    setSubmittingExam(true);

    const correctCount = questions.filter(
      (q) => answers[q.id] === q.correct_answer
    ).length;

    const incorrectCount = questions.length - correctCount;

    const accuracyPct =
      questions.length > 0
        ? Math.round((correctCount / questions.length) * 100)
        : 0;

    const answerRows = questions.map((q) => {
      const selectedAnswer = answers[q.id];

      return {
        attempt_id: attemptId,
        question_id: q.question_number,
        selected_answer:
          selectedAnswer === undefined ? null : selectedAnswer,
        correct_answer: q.correct_answer,
        is_correct:
          selectedAnswer === undefined ? false : selectedAnswer === q.correct_answer,
        topic: q.topic || null,
        domain: q.domain || null,
        difficulty: q.difficulty || null,
      };
    });

    const { error: answersError } = await supabase
      .from("exam_answers")
      .insert(answerRows);

    if (answersError) {
      alert(answersError.message);
      setSubmittingExam(false);
      return false;
    }

    const { error: attemptError } = await supabase
      .from("exam_attempts")
      .update({
        submitted_at: new Date().toISOString(),
        score: correctCount,
        correct_count: correctCount,
        incorrect_count: incorrectCount,
        accuracy_pct: accuracyPct,
        attempt_status: "submitted",
      })
      .eq("id", attemptId);

    if (attemptError) {
      alert(attemptError.message);
      setSubmittingExam(false);
      return false;
    }
    if (retakePermissionId) {
      const { error: retakeError } = await supabase
        .from("exam_retake_permissions")
        .update({
          used_at: new Date().toISOString(),
          status: "used",
        })
        .eq("id", retakePermissionId);

      if (retakeError) {
        alert(retakeError.message);
        setSubmittingExam(false);
        return false;
      }
    }
    setSubmittingExam(false);
    return true;
  };

  const fetchAttemptAnswers = async (attemptId) => {
    if (!attemptId) return [];

    setLoadingViewReport(true);

    const { data, error } = await supabase
      .from("exam_answers")
      .select(
        "question_id, selected_answer, correct_answer, is_correct, topic, domain, difficulty"
      )
      .eq("attempt_id", attemptId)
      .order("question_id", { ascending: true });

    if (error) {
      alert(error.message);
      setViewReportAnswers([]);
      setLoadingViewReport(false);
      return [];
    }

    setViewReportAnswers(data || []);
    setLoadingViewReport(false);

    return data || [];
  };

  return {
    currentAttemptId,
    submittingExam,
    startExamAttempt,
    submitExamAttempt,

    viewReportAnswers,
    loadingViewReport,
    fetchAttemptAnswers,
  };
}